import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from './useAuth';
import { useMemo } from 'react';

export type MarginWarning = 'no_cost' | 'zero_margin' | 'negative_margin' | 'very_high' | null;

export interface AssetItem {
  product_id: string;
  product_name: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  location_id: string | null;
  location_name: string | null;
  quantity_on_hand: number;
  quantity_foh: number;
  quantity_boh: number;
  unit_cost: number;
  average_cost: number;
  total_cost: number;
  retail_price: number;
  total_retail: number;
  margin_dollars: number;
  margin_percent: number;
  margin_warning: MarginWarning;
  // Historical data
  items_sold: number;
  revenue: number;
  last_sale_date: string | null;
  // Inbound
  inbound_inventory: number;
  in_transit: number;
}

export interface ProductGroup {
  product_id: string;
  product_name: string;
  sku: string | null;
  category_id: string | null;
  category_name: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  // Totals across all locations
  total_quantity: number;
  total_cost: number;
  total_retail: number;
  average_cost: number;
  retail_price: number;
  margin_percent: number;
  margin_warning: MarginWarning;
  // Historical totals
  total_items_sold: number;
  total_revenue: number;
  last_sale_date: string | null;
  // Inbound totals
  total_inbound: number;
  // Location breakdown
  locations: AssetItem[];
  location_count: number;
  // UI state
  isExpanded?: boolean;
}

export interface AssetReportSummary {
  total_items: number;
  total_units: number;
  total_cost: number;
  total_retail: number;
  average_margin: number;
  potential_profit: number;
  items_needing_review: number;
}

export interface AssetReportFilters {
  locationId?: string;
  categoryId?: string;
  vendorId?: string;
  includeZero?: boolean;
  sortBy?: 'name' | 'margin_asc' | 'margin_desc' | 'value_desc' | 'sold_desc';
  dateFrom?: Date;
  dateTo?: Date;
}

export function useAssetReport(filters: AssetReportFilters = {}) {
  const { organization } = useAuth();
  const organizationId = organization?.id;

  // Fetch enhanced asset report data (location-level detail)
  const {
    data: rawAssetItems,
    isLoading: itemsLoading,
    error: itemsError,
    refetch,
  } = useQuery({
    queryKey: ['enhanced-asset-report', organizationId, filters],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase.rpc('get_enhanced_asset_report', {
        p_organization_id: organizationId,
        p_location_id: filters.locationId || null,
        p_include_zero: filters.includeZero ?? false,
        p_category_id: filters.categoryId || null,
        p_vendor_id: filters.vendorId || null,
        p_date_from: filters.dateFrom?.toISOString() || null,
        p_date_to: filters.dateTo?.toISOString() || null,
      });

      if (error) throw error;
      return (data || []) as AssetItem[];
    },
    enabled: !!organizationId,
  });

  // Fetch summary data
  const {
    data: summary,
    isLoading: summaryLoading,
  } = useQuery({
    queryKey: ['asset-report-summary', organizationId, filters.locationId],
    queryFn: async () => {
      if (!organizationId) return null;

      const { data, error } = await supabase.rpc('get_asset_report_summary', {
        p_organization_id: organizationId,
        p_location_id: filters.locationId || null,
      });

      if (error) throw error;
      return (data?.[0] || null) as AssetReportSummary | null;
    },
    enabled: !!organizationId,
  });

  // Group items by product with location breakdown
  const groupedProducts = useMemo(() => {
    if (!rawAssetItems?.length) return [];

    const productMap = new Map<string, ProductGroup>();

    for (const item of rawAssetItems) {
      const existing = productMap.get(item.product_id);

      if (existing) {
        // Add to existing product group
        existing.locations.push(item);
        existing.total_quantity += item.quantity_on_hand;
        existing.total_cost += item.total_cost;
        existing.total_retail += item.total_retail;
        existing.total_items_sold += item.items_sold;
        existing.total_revenue += item.revenue;
        existing.total_inbound += item.inbound_inventory;
        existing.location_count++;
        // Update last sale date if this one is more recent
        if (item.last_sale_date && (!existing.last_sale_date || item.last_sale_date > existing.last_sale_date)) {
          existing.last_sale_date = item.last_sale_date;
        }
      } else {
        // Create new product group
        productMap.set(item.product_id, {
          product_id: item.product_id,
          product_name: item.product_name,
          sku: item.sku,
          category_id: item.category_id,
          category_name: item.category_name,
          vendor_id: item.vendor_id,
          vendor_name: item.vendor_name,
          total_quantity: item.quantity_on_hand,
          total_cost: item.total_cost,
          total_retail: item.total_retail,
          average_cost: item.average_cost,
          retail_price: item.retail_price,
          margin_percent: item.margin_percent,
          margin_warning: item.margin_warning as MarginWarning,
          total_items_sold: item.items_sold,
          total_revenue: item.revenue,
          last_sale_date: item.last_sale_date,
          total_inbound: item.inbound_inventory,
          locations: [item],
          location_count: 1,
          isExpanded: false,
        });
      }
    }

    return Array.from(productMap.values());
  }, [rawAssetItems]);

  // Sort grouped products
  const sortedProducts = useMemo(() => {
    const products = [...groupedProducts];

    switch (filters.sortBy) {
      case 'margin_asc':
        return products.sort((a, b) => a.margin_percent - b.margin_percent);
      case 'margin_desc':
        return products.sort((a, b) => b.margin_percent - a.margin_percent);
      case 'value_desc':
        return products.sort((a, b) => b.total_cost - a.total_cost);
      case 'sold_desc':
        return products.sort((a, b) => b.total_items_sold - a.total_items_sold);
      case 'name':
      default:
        return products.sort((a, b) => a.product_name.localeCompare(b.product_name));
    }
  }, [groupedProducts, filters.sortBy]);

  // Flat list for backwards compatibility and simple views
  const assetItems = rawAssetItems || [];

  // Filter items with warnings
  const itemsWithWarnings = sortedProducts.filter((item) => item.margin_warning !== null);

  // Get warning counts
  const warningCounts = {
    no_cost: sortedProducts.filter((item) => item.margin_warning === 'no_cost').length,
    zero_margin: sortedProducts.filter((item) => item.margin_warning === 'zero_margin').length,
    negative_margin: sortedProducts.filter((item) => item.margin_warning === 'negative_margin').length,
    very_high: sortedProducts.filter((item) => item.margin_warning === 'very_high').length,
  };

  // Group by category for analysis
  const groupedByCategory = sortedProducts.reduce((acc, item) => {
    const categoryId = item.category_id || 'uncategorized';
    const categoryName = item.category_name || 'Uncategorized';
    if (!acc[categoryId]) {
      acc[categoryId] = {
        categoryId,
        categoryName,
        items: [],
        totalCost: 0,
        totalRetail: 0,
        totalUnits: 0,
      };
    }
    acc[categoryId].items.push(item);
    acc[categoryId].totalCost += item.total_cost;
    acc[categoryId].totalRetail += item.total_retail;
    acc[categoryId].totalUnits += item.total_quantity;
    return acc;
  }, {} as Record<string, { categoryId: string; categoryName: string; items: ProductGroup[]; totalCost: number; totalRetail: number; totalUnits: number }>);

  // Export to CSV
  const exportToCSV = () => {
    if (!assetItems.length) return '';

    const headers = [
      'Product Name',
      'SKU',
      'Category',
      'Vendor',
      'Location',
      'Quantity',
      'Unit Cost',
      'Average Cost',
      'Total Cost',
      'Retail Price',
      'Total Retail',
      'Margin %',
      'Items Sold',
      'Revenue',
      'Last Sale',
      'Inbound',
      'Warning',
    ];

    const rows = assetItems.map((item) => [
      item.product_name,
      item.sku || '',
      item.category_name || '',
      item.vendor_name || '',
      item.location_name || '',
      item.quantity_on_hand,
      item.unit_cost.toFixed(2),
      item.average_cost.toFixed(2),
      item.total_cost.toFixed(2),
      item.retail_price.toFixed(2),
      item.total_retail.toFixed(2),
      item.margin_percent.toFixed(2),
      item.items_sold,
      item.revenue.toFixed(2),
      item.last_sale_date ? new Date(item.last_sale_date).toLocaleDateString() : '',
      item.inbound_inventory,
      item.margin_warning || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    return csvContent;
  };

  const downloadCSV = () => {
    const csvContent = exportToCSV();
    if (!csvContent) return;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asset-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return {
    // Grouped data (for Lightspeed-style display)
    groupedProducts: sortedProducts,
    // Flat data (for simple table or backwards compatibility)
    assetItems,
    itemsWithWarnings,
    warningCounts,
    groupedByCategory: Object.values(groupedByCategory),
    summary,
    isLoading: itemsLoading || summaryLoading,
    error: itemsError,
    refetch,
    exportToCSV,
    downloadCSV,
  };
}
